import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite } from "../../context/SiteContext";
import { storyPillars, storySegments } from "../../data/homeContent";
import { AgaveMark } from "../culture/AgaveMark";
import { Calavera } from "../culture/Calavera";
import { TalaveraCorner } from "../culture/TalaveraCorner";
export type StoryStyle = "highlight" | "words" | "split";
type CultureStoryProps = {
  variant?: StoryStyle;
  standalone?: boolean;
};
const EASE_OUT = [0.23, 1, 0.32, 1] as const;
export function CultureStory({
  variant = "highlight",
  standalone = false
}: CultureStoryProps) {
  if (variant === "words") return <WordsStory standalone={standalone} data-id="element-246" data-mp="2mwniyho2vwuh" />;
  if (variant === "split") return <SplitStory standalone={standalone} data-id="element-247" data-mp="2rvqgzi5n52sy" />;
  return <HighlightStory standalone={standalone} data-id="element-248" data-mp="2rvi25kpyjnf4" />;
}
function Heading({
  standalone,
  className,
  children
}: {
  standalone: boolean;
  className: string;
  children: React.ReactNode;
}) {
  return standalone ? <h1 id="culture-heading" className={className} data-id="element-249" data-mp="1axzw6lw4ych3">{children}</h1> : <h2 id="culture-heading" className={className} data-id="element-250" data-mp="1oq63dihb994e">{children}</h2>;
}
function VisitButton({
  tone
}: {
  tone: "green" | "cream";
}) {
  const {
    t
  } = useSite();
  return <Link to="/locations" className={`inline-flex min-h-12 items-center gap-2 rounded-full px-7 font-bold transition-[background-color,transform] duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marigold ${tone === "green" ? "bg-agavedeep text-cream hover:bg-[#0B2C22]" : "bg-cream text-agavedeep hover:bg-white"}`} data-id="element-251" data-mp="w0z2icudw7fz">
      {t("Visit Us", "Visítanos")}
      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-252" data-mp="2hdaabszyrxit" />
    </Link>;
}

/* Option 1 — Highlighted statement: big two-tone headline, story set large with key phrases brushed in marigold. */
function HighlightStory({
  standalone
}: {
  standalone: boolean;
}) {
  const {
    t
  } = useSite();
  const reduceMotion = useReducedMotion();
  let highlightIndex = 0;
  return <section aria-labelledby="culture-heading" className="relative overflow-hidden bg-cream px-5 py-24 sm:px-8 sm:py-32" data-id="element-253" data-mp="2tgczgor7gjaf">
      <TalaveraCorner className="absolute left-3 top-3 w-20 sm:left-6 sm:top-6 sm:w-36" data-id="element-254" data-mp="2yewhie2ohaxi" />
      <TalaveraCorner className="absolute bottom-3 right-3 w-20 rotate-180 sm:bottom-6 sm:right-6 sm:w-36" data-id="element-255" data-mp="20t8nu0xlx6po" />

      <div className="relative mx-auto max-w-4xl" data-id="element-256" data-mp="25s239ywgf7sh">
        <AgaveMark className="h-10 w-[60px]" data-id="element-257" data-mp="2aqdeacyhjoca" />
        <Heading standalone={standalone} className="mt-5 font-serif text-[48px] font-bold leading-[0.92] text-agavedeep sm:text-7xl lg:text-8xl" data-id="element-258" data-mp="2aplgqwlbvgjd">
          {t("More Than Food.", "Más que comida.")}
          <span className="block italic text-chili" data-id="element-259" data-mp="1i0wn1imlqdhn">{t("A Taste of Mexico.", "Un sabor de México.")}</span>
        </Heading>

        <p className="mt-10 font-serif text-[26px] font-semibold leading-[1.3] text-agavedeep sm:text-4xl sm:leading-[1.25]" data-id="element-260" data-mp="1myy2zuoqdwow">
          {storySegments.map(segment => {
          const text = t(segment.en, segment.es);
          if (!segment.highlight) return <React.Fragment key={segment.en} data-id="element-261" data-mp="15iar75r4z9qj">{text}</React.Fragment>;
          const delay = 0.1 + highlightIndex++ * 0.12;
          return <span key={segment.en} className="relative whitespace-normal" data-id="element-262" data-mp="10m3ix6b9pm5f">
                <motion.span aria-hidden="true" initial={reduceMotion ? false : {
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true,
              amount: 0.8
            }} transition={{
              duration: 0.3,
              ease: EASE_OUT,
              delay
            }} className="absolute inset-x-0 bottom-[0.08em] -z-0 h-[0.38em] origin-left rounded-sm bg-marigold/45" data-id="element-263" data-mp="wjyr8d4dqzlf" />
                <span className="relative" data-id="element-264" data-mp="8stgudh7kzii">{text}</span>
              </span>;
        })}
        </p>

        <ul className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-2 font-serif text-3xl font-bold italic sm:text-4xl" data-id="element-265" data-mp="xiz2w0udtjjq">
          {storyPillars.map((pillar, index) => <li key={pillar[0]} className="flex items-center gap-4" data-id="element-266" data-mp="12kerg2vrq86v" data-mpi={index}>
              <span className={index === 0 ? "text-chili" : index === 1 ? "text-talavera" : "text-marigold"} data-id="element-267" data-mp="oqx4e83vidc3">{t(...pillar)}</span>
              {index < storyPillars.length - 1 && <span aria-hidden="true" className="h-2 w-2 rotate-45 bg-turquoise" data-id="element-268" data-mp="espzinmuzlqg" />}
            </li>)}
        </ul>

        <div className="mt-10" data-id="element-269" data-mp="jtnhaz3yidrf">
          <VisitButton tone="green" data-id="element-270" data-mp="jppzcgt47s94" />
        </div>
      </div>
    </section>;
}

/* Option 2 — Scroll-lit words: the story lights up word by word as you scroll, on deep agave green. */
function WordsStory({
  standalone
}: {
  standalone: boolean;
}) {
  const {
    t
  } = useSite();
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start start", "end end"]
  });
  const words = storySegments.flatMap(segment => t(segment.en, segment.es).split(" ").filter(Boolean).map(word => ({
    word,
    highlight: Boolean(segment.highlight)
  })));
  return <section aria-labelledby="culture-heading" className="relative bg-agavedeep text-cream" data-id="element-271" data-mp="2bop9avqcql2">
      <div ref={ref} className={reduceMotion ? "" : "relative h-[220svh]"} data-id="element-272" data-mp="3tni37g1t5xt3">
        <div className={`${reduceMotion ? "" : "sticky top-[68px] h-[calc(100svh-68px)]"} flex items-center px-5 py-20 sm:px-8`} data-id="element-273" data-mp="c7ib5kvc9wfi">
          <div className="mx-auto max-w-4xl" data-id="element-274" data-mp="79723ccvbpyc">
            <div className="flex items-center gap-3" data-id="element-275" data-mp="l9rkw8is9lru">
              <AgaveMark className="h-8 w-12" data-id="element-276" data-mp="l8gee0xydzx1" />
              <Heading standalone={standalone} className="font-serif text-2xl font-bold italic text-marigold sm:text-3xl" data-id="element-277" data-mp="g9myy2z3vyu8">
                {t("More Than Food. A Taste of Mexico.", "Más que comida. Un sabor de México.")}
              </Heading>
            </div>
            <p className="mt-8 font-serif text-[30px] font-semibold leading-[1.22] sm:text-5xl sm:leading-[1.15]" data-id="element-278" data-mp="2i3w3qlwhf5hd">
              {words.map((item, index) => <ScrollWord key={`${item.word}-${index}`} progress={scrollYProgress} range={[index / words.length, (index + 1) / words.length]} highlight={item.highlight} still={Boolean(reduceMotion)} data-id="element-279" data-mp="qy9ca75evu37" data-mpi={index}>
                  {item.word}
                </ScrollWord>)}
            </p>
            <div className="mt-10" data-id="element-280" data-mp="19v1454z0rhiv">
              <VisitButton tone="cream" data-id="element-281" data-mp="19tzgm10th2k9" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ScrollWord({
  progress,
  range,
  highlight,
  still,
  children
}: {
  progress: MotionValue<number>;
  range: [number, number];
  highlight: boolean;
  still: boolean;
  children: string;
}) {
  const opacity = useTransform(progress, [range[0] * 0.85, range[1] * 0.85], [0.22, 1]);
  return <motion.span style={still ? undefined : {
    opacity
  }} className={highlight ? "text-marigold" : "text-cream"} data-id="element-282" data-mp="3d4m7ywznco40">
      {children}{" "}
    </motion.span>;
}

/* Option 3 — Split poster: giant stacked headline on marigold with a bold pull quote. */
function SplitStory({
  standalone
}: {
  standalone: boolean;
}) {
  const {
    t
  } = useSite();
  const reduceMotion = useReducedMotion();
  const lines: [string, string][] = [["More", "Más"], ["Than", "que"], ["Food.", "comida."]];
  return <section aria-labelledby="culture-heading" className="relative overflow-hidden bg-marigold px-5 py-20 text-agavedeep sm:px-8 sm:py-28" data-id="element-283" data-mp="3rt8r79sea00i">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_1fr] lg:items-center" data-id="element-284" data-mp="3n1h5bck6oigp">
        <Heading standalone={standalone} className="font-serif text-[76px] font-bold leading-[0.82] sm:text-[120px] lg:text-[150px]" data-id="element-285" data-mp="3i3fpr1lyv19z">
          {lines.map((line, index) => <motion.span key={line[0]} initial={reduceMotion ? false : {
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.6
        }} transition={{
          duration: 0.3,
          ease: EASE_OUT,
          delay: index * 0.08
        }} className={`block ${index === 2 ? "italic text-chili" : ""}`} data-id="element-286" data-mp="em7b0qmjchjh" data-mpi={index}>
              {t(...line)}
            </motion.span>)}
          <span className="mt-4 block font-serif text-3xl italic text-agavedeep sm:text-4xl" data-id="element-287" data-mp="5q5endlv8kq6">
            {t("A Taste of Mexico.", "Un sabor de México.")}
          </span>
        </Heading>

        <div className="border-t-2 border-agavedeep/25 pt-8 lg:border-l-2 lg:border-t-0 lg:pl-12 lg:pt-0" data-id="element-288" data-mp="amvtxypujcaj">
          <Calavera className="h-12 w-10" data-id="element-289" data-mp="r281oq8m6g3" />
          <blockquote className="mt-5 font-serif text-[32px] font-bold italic leading-[1.1] sm:text-5xl" data-id="element-290" data-mp="q0u9chu75uo">
            {t("“Make every visit feel like a fiesta worth remembering.”", "“Que cada visita se sienta como una fiesta digna de recordar.”")}
          </blockquote>
          <p className="mt-6 max-w-lg text-lg leading-8 text-agavedeep/85" data-id="element-291" data-mp="2dx8y1ev0n4sa">
            {t("Every plate is inspired by the flavors, traditions and hospitality of Mexico — from tacos shared around the table to unforgettable celebrations with family and friends.", "Cada plato está inspirado en los sabores, tradiciones y hospitalidad de México — desde tacos compartidos alrededor de la mesa hasta celebraciones inolvidables con familia y amigos.")}
          </p>
          <div className="mt-8" data-id="element-292" data-mp="1q8po6uwbijy3">
            <VisitButton tone="green" data-id="element-293" data-mp="1q9r174ww9gig" />
          </div>
        </div>
      </div>
    </section>;
}